import { ChangeDetectionStrategy, Component, OnInit, ViewChild } from "@angular/core";
import { UntypedFormBuilder } from "@angular/forms";
import { MatDialog } from "@angular/material/dialog";
import { MatSnackBar } from "@angular/material/snack-bar";
import { ActivatedRoute, Router } from "@angular/router";
import moment from "moment";
import { RouteType } from "src/app/models/enum";
import { FeedbackTaskDetail, ReportSummary, SignOffPopup } from "src/app/models/models";
import { CbtaModalComponent } from "src/app/shared/components/cbta-modal/cbta-modal.component";
import { confirmCompleteAssessment } from "src/app/shared/utils/confirmation-dialog.utils";
import {
	ErrorSnackbarComponent,
	ErrorSnackbarData,
} from "src/app/shared/components/cbta-snackbar/error-snackbar.component";
import { HttpService } from "src/app/shared/services/http.service";
import { LoaderService } from "src/app/shared/services/loader.service";
import { ProfileService } from "src/app/shared/services/profile.service";
import { SharedService } from "src/app/shared/services/shared.service";
import { isTs1V4RatingFormVersion } from "../ratings/container-ratings-form/shared-v2/utils/rating-form-version.utils";
import {
	Ts1PreviewCandidateDetails,
	Ts1ReviewCompetencyBlock,
	Ts1ReviewManeuverRow,
} from "../ratings/container-ratings-form/ts1-rating-form-v3";
import { buildTs1ReportPreviewContext, isTs1V3ReportSummary } from "../report/report-summary/ts1-report-preview.utils";
import { ReportSummaryComponent } from "../report/report-summary/report-summary.component";
import { ChecklistCandidateSignoffComponent } from "./checklist-candidate-signoff.component";
import {
	buildChecklistInstructorSignoff,
	ChecklistInstructorSignoffView,
	ChecklistStagingDetail,
} from "./ts1-staging-preview.utils";
import {
	isV2BundleRemoteSignoffReportSummary,
	normalizeChecklistReportSummary,
} from "./v2-bundle-staging-preview.utils";

type ChecklistStep = "preview" | "maneuvers" | "signoff";

@Component({
	selector: "app-checklist-detail",
	templateUrl: "./checklist-detail.component.html",
	styleUrls: ["./checklist-detail.component.scss"],
	changeDetection: ChangeDetectionStrategy.Eager,
	standalone: false,
})
export class ChecklistDetailComponent implements OnInit {
	trainingAssessmentId = "";
	stagingDetail: ChecklistStagingDetail | null = null;
	reportSummary: ReportSummary | null = null;
	candidateId = "";
	step: ChecklistStep = "preview";
	previewStepIndex = 0;
	previewContext: ReturnType<typeof buildTs1ReportPreviewContext> | null = null;
	isV2BundleChecklist = false;
	instructorSignoff: ChecklistInstructorSignoffView | null = null;
	loadError = "";
	candidateIndex = 0;
	signing = false;

	candidateSignaturePreview = "";
	candidateSignedAt = "";
	pendingSignOff: SignOffPopup | null = null;
	pendingSignature: Blob | null = null;
	feedbackTask: FeedbackTaskDetail | null = null;
	feedbackFormValid = true;

	@ViewChild(ChecklistCandidateSignoffComponent)
	private signoffComponent?: ChecklistCandidateSignoffComponent;

	@ViewChild(ReportSummaryComponent)
	private reportSummaryComponent?: ReportSummaryComponent;

	constructor(
		private route: ActivatedRoute,
		private router: Router,
		private httpService: HttpService,
		private loaderService: LoaderService,
		public sharedService: SharedService,
		private profileService: ProfileService,
		private dialog: MatDialog,
		private fb: UntypedFormBuilder,
		private snackBar: MatSnackBar,
	) {}

	ngOnInit(): void {
		this.sharedService.setActivePage(RouteType.CHECKLIST);
		this.route.params.subscribe((params) => {
			this.trainingAssessmentId = params["trainingAssessmentId"];
			if (this.trainingAssessmentId) {
				this.loadDetail();
			}
		});
	}

	get candidateName(): string {
		return (
			this.previewContext?.candidateDetails?.name ??
			this.previewContext?.candidateNames?.[0] ??
			this.reportSummary?.candidateDetails?.name ??
			""
		);
	}

	/** Authenticated pilot staff number — used to lock checklist sign-off identity. */
	private get authenticatedStaffNumber(): string {
		const fromProfile = (this.profileService.profile?.staff_number ?? "").toString().trim();
		if (fromProfile) return fromProfile;
		const fromPreview = (this.previewContext?.candidateDetails?.staffNumber ?? "").toString().trim();
		return fromPreview && fromPreview !== "-" ? fromPreview : "";
	}

	loadDetail(): void {
		this.loaderService.setLoading(true);
		this.loadError = "";
		this.previewContext = null;
		this.isV2BundleChecklist = false;
		this.stagingDetail = null;
		this.reportSummary = null;
		this.candidateId = "";
		this.step = "preview";
		this.resetCandidateSignature();

		this.httpService.getCandidateChecklistDetail(this.trainingAssessmentId).subscribe({
			next: (resp) => {
				try {
					const detail = (resp?.data ?? resp) as ChecklistStagingDetail & {
						reportSummary?: ReportSummary;
						candidateId?: string;
						remoteSignOff?: ChecklistStagingDetail["remoteSignOff"];
						feedback?: FeedbackTaskDetail | null;
					};

					if (!detail.reportSummary || !detail.candidateId) {
						this.loadError = "Unable to load checklist preview.";
						return;
					}

					if (
						!isTs1V3ReportSummary(detail.reportSummary) &&
						!isV2BundleRemoteSignoffReportSummary(detail.reportSummary)
					) {
						this.loadError = "Unable to load checklist preview.";
						return;
					}

					this.stagingDetail = detail;
					this.isV2BundleChecklist = isV2BundleRemoteSignoffReportSummary(detail.reportSummary);
					this.reportSummary = this.isV2BundleChecklist
						? normalizeChecklistReportSummary(detail.reportSummary)
						: detail.reportSummary;
					this.candidateId = detail.candidateId;
					this.feedbackTask = detail.feedback ?? null;
					this.candidateIndex = detail.candidateIndex ?? 0;
					this.step = "preview";
					this.previewStepIndex = 0;

					if (!this.isV2BundleChecklist) {
						this.previewContext = buildTs1ReportPreviewContext(detail.reportSummary, this.fb, detail.candidateId);
					}
					this.instructorSignoff = buildChecklistInstructorSignoff(detail);
					this.feedbackFormValid = !this.feedbackPending;
				} catch {
					this.loadError = "Unable to load checklist preview.";
				} finally {
					this.loaderService.setLoading(false);
				}
			},
			error: () => {
				this.loaderService.setLoading(false);
				this.loadError = "This checklist is no longer pending sign-off.";
			},
		});
	}

	readonly blocksForIndex = (_index: number): Ts1ReviewCompetencyBlock[] => this.previewContext?.competencyBlocks ?? [];

	readonly rowsForIndex = (_index: number): Ts1ReviewManeuverRow[] => this.previewContext?.maneuverRows ?? [];

	readonly commentsForIndex = (_index: number) => this.previewContext?.summaryComments ?? [];

	readonly obCommentsForIndex = (_index: number) =>
		this.previewContext?.competencyObComments ?? this.previewContext?.summaryComments ?? [];

	readonly detailsForIndex = (_index: number): Ts1PreviewCandidateDetails | null =>
		this.previewContext?.candidateDetails ?? null;

	readonly reminderNotesEnabledForPreview = (): boolean => !!this.previewContext?.reminderNotesEnabled;

	readonly structuredReminderNotesForPreview = (): boolean =>
		!!this.previewContext?.useStructuredReminderNotes;

	readonly traineeAssessmentIdForIndex = () => this.trainingAssessmentId;

	get hasManeuverBreakdown(): boolean {
		return (this.reportSummary?.ts1V3PreviewSummary?.maneuverBreakdownView?.length ?? 0) > 0;
	}

	get isV4Layout(): boolean {
		return isTs1V4RatingFormVersion(this.reportSummary?.formVersion);
	}

	goBack(): void {
		this.router.navigate([RouteType.CHECKLIST]);
	}

	onPreviewBack(): void {
		this.previewStepIndex = 0;
		this.goBack();
	}

	onPreviewNext(): void {
		if (this.isV2BundleChecklist) {
			this.previewStepIndex = this.reportSummaryComponent?.getSelectedStepIndex() ?? 0;
		}
		this.step = this.hasManeuverBreakdown ? "maneuvers" : "signoff";
		this.scrollToTop();
	}

	onManeuversPrevious(): void {
		this.previewStepIndex = 0;
		this.step = "preview";
		this.scrollToTop();
	}

	onManeuversNext(): void {
		this.step = "signoff";
		this.scrollToTop();
	}

	onSignoffBack(): void {
		this.step = this.hasManeuverBreakdown && !this.isV2BundleChecklist ? "maneuvers" : "preview";
		this.scrollToTop();
	}

	private scrollToTop(): void {
		setTimeout(() => {
			const element = document.getElementById("cbta-web-page-container");
			element?.scrollTo({ top: 0, behavior: "smooth" });
		});
	}

	get feedbackPending(): boolean {
		if (!this.feedbackTask?.template) return false;
		return this.feedbackTask.status !== "completed" && !this.feedbackTask.submitted;
	}

	get isCompleteDisabled(): boolean {
		return !this.pendingSignature || !this.pendingSignOff || (this.feedbackPending && !this.feedbackFormValid);
	}

	onFeedbackCompleted(): void {
		if (this.feedbackTask) {
			this.feedbackTask = { ...this.feedbackTask, status: "completed", submitted: true };
		}
		this.feedbackFormValid = true;
	}

	openCandidateSignOff(): void {
		if (!navigator.onLine || this.signing) return;

		const lockedStaffNumber = this.authenticatedStaffNumber;
		if (!lockedStaffNumber) {
			this.loadError = "Unable to determine your staff number for sign-off.";
			return;
		}

		const dialogRef = this.dialog.open(CbtaModalComponent, {
			panelClass: "signoff-modal",
			height: "400px",
			width: "460px",
			hasBackdrop: true,
			data: {
				modalName: "signOffModal",
				modalTitle: "Sign Off",
				modalContent: "",
				modalBtnConfirmText: "Sign",
				modalBtnCancelText: "Cancel",
				disableClose: true,
				lockedStaffNumber,
			},
		});

		dialogRef.afterClosed().subscribe((result: { signOffData?: SignOffPopup } | undefined) => {
			const signOffData = result?.signOffData;
			if (!signOffData?.secretKey) return;

			const validationPayload: SignOffPopup = {
				...signOffData,
				staffNumber: lockedStaffNumber,
				traineeAssessmentIdReference: this.trainingAssessmentId,
			};

			this.httpService.candidateSignatureValidation(validationPayload).subscribe({
				next: () => this.openSignaturePad(validationPayload),
				error: () => {},
			});
		});
	}

	async completeSignOff(): Promise<void> {
		if (this.isCompleteDisabled || this.signing) return;
		if (!(await confirmCompleteAssessment(this.dialog))) return;

		const signOffData = this.pendingSignOff;
		const signature = this.pendingSignature;
		if (!signOffData || !signature) return;

		const submitSignOff = () => this.submitRemoteSignOff(signOffData, signature);

		if (this.feedbackPending && this.signoffComponent) {
			this.signing = true;
			this.signoffComponent.submitFeedbackIfNeeded().subscribe({
				next: () => submitSignOff(),
				error: () => {
					this.signing = false;
					this.loaderService.setLoading(false);
					this.loadError = "Unable to submit feedback. Sign-off was not completed.";
					this.snackBar.openFromComponent(ErrorSnackbarComponent, {
						data: { message: this.loadError } as ErrorSnackbarData,
						duration: 5000,
						horizontalPosition: "center",
						verticalPosition: "top",
						panelClass: ["error-snackbar"],
					});
				},
			});
			return;
		}

		submitSignOff();
	}

	private resetCandidateSignature(): void {
		this.candidateSignaturePreview = "";
		this.candidateSignedAt = "";
		this.pendingSignOff = null;
		this.pendingSignature = null;
	}

	private openSignaturePad(signOffData: SignOffPopup): void {
		this.httpService.getMySignature().subscribe({
			next: (resp) => {
				const bytes = resp?.data?.signature?.data;
				this.showSignatureModal(bytes, signOffData);
			},
			error: () => this.showSignatureModal(undefined, signOffData),
		});
	}

	private showSignatureModal(signatureBytes: number[] | Uint8Array | undefined, signOffData: SignOffPopup): void {
		const baseDialogData = {
			modalTitle: "Signature",
			modalName: "signaturePadModal",
			modalBtnConfirmText: "Submit",
			modalBtnCancelText: "Cancel",
		};

		const openDialog = (dialogData: Record<string, unknown>) => {
			const dialogRef = this.dialog.open(CbtaModalComponent, {
				panelClass: "signature-pad-modal",
				height: "420px",
				width: "528px",
				hasBackdrop: true,
				data: dialogData,
			});

			dialogRef
				.afterClosed()
				.subscribe((result: { close?: boolean; signature?: { file?: Blob; dataUrl?: string } }) => {
					if (!result?.close || !result.signature?.file) return;
					this.pendingSignOff = signOffData;
					this.pendingSignature = result.signature.file;
					this.candidateSignaturePreview = result.signature.dataUrl ?? "";
					this.candidateSignedAt = moment().format("DD/MM/YY HH:mm:ss");
				});
		};

		if (!signatureBytes) {
			openDialog(baseDialogData);
			return;
		}

		const uint8Array = new Uint8Array(signatureBytes);
		const blob = new Blob([uint8Array], { type: "image/png" });
		const file = new File([blob], "signature.png", { type: "image/png" });
		const reader = new FileReader();
		reader.onload = () => {
			openDialog({
				...baseDialogData,
				signatureData: { dataUrl: reader.result as string, file },
			});
		};
		reader.readAsDataURL(blob);
	}

	private submitRemoteSignOff(signOffData: SignOffPopup, signature: Blob): void {
		const staffNumber = (signOffData.staffNumber ?? "").toString().trim();
		if (!staffNumber || !signOffData.secretKey) {
			this.loadError = "Unable to complete sign-off: missing staff number or PIN.";
			return;
		}

		this.signing = true;
		this.loaderService.setLoading(true);

		this.httpService
			.candidateRemoteSignOff({
				trainingAssessmentId: this.trainingAssessmentId,
				secretKey: signOffData.secretKey,
				staffNumber,
				trainee_signature: signature,
			})
			.subscribe({
				next: (resp) => {
					this.signing = false;
					this.loaderService.setLoading(false);
					const payload = resp?.data ?? resp;
					if (payload?.signed) {
						this.router.navigate([RouteType.CHECKLIST]);
					}
				},
				error: () => {
					this.signing = false;
					this.loaderService.setLoading(false);
				},
			});
	}
}
